import { useEffect } from "react";

interface UsePopoverKeynavArgs<TSlash, TUser, TEmoji> {
  /** True when this composer is in a hidden multiplexed slot. The
   *  document-level listener is skipped — otherwise N kept-alive
   *  composers would all preventDefault and steal keystrokes. */
  hidden: boolean;

  /** Giphy preview cancel — consumes Escape when the preview is open. */
  giphyPreview: { searchTerm: string } | null;
  onCancelGiphy: () => void;

  // Slash commands
  showSlashCommands: boolean;
  setShowSlashCommands: (open: boolean) => void;
  filteredSlashCommands: TSlash[];
  slashIndex: number;
  setSlashIndex: (next: number | ((prev: number) => number)) => void;
  insertSlashCommand: (cmd: TSlash) => void;

  // Mentions
  showMentions: boolean;
  setShowMentions: (open: boolean) => void;
  filteredUsers: TUser[];
  mentionIndex: number;
  setMentionIndex: (next: number | ((prev: number) => number)) => void;
  insertMention: (user: TUser) => void;

  // Emoji shortcodes
  showEmojiShortcodes: boolean;
  setShowEmojiShortcodes: (open: boolean) => void;
  filteredEmojis: TEmoji[];
  emojiShortcodeIndex: number;
  setEmojiShortcodeIndex: (next: number | ((prev: number) => number)) => void;
  insertEmojiShortcode: (emoji: TEmoji) => void;
}

/**
 * Document-level keydown listener that drives navigation across all
 * three composer popovers (slash / mention / emoji). Arrow keys move
 * the highlight; Tab/Enter inserts; Escape closes. Escape also
 * cancels an open Giphy preview as a top-priority shortcut.
 *
 * Skipped entirely on hidden multiplexed instances — see the
 * `hidden`-param doc above for why.
 */
export function usePopoverKeynav<TSlash, TUser, TEmoji>({
  hidden,
  giphyPreview,
  onCancelGiphy,
  showSlashCommands,
  setShowSlashCommands,
  filteredSlashCommands,
  slashIndex,
  setSlashIndex,
  insertSlashCommand,
  showMentions,
  setShowMentions,
  filteredUsers,
  mentionIndex,
  setMentionIndex,
  insertMention,
  showEmojiShortcodes,
  setShowEmojiShortcodes,
  filteredEmojis,
  emojiShortcodeIndex,
  setEmojiShortcodeIndex,
  insertEmojiShortcode,
}: UsePopoverKeynavArgs<TSlash, TUser, TEmoji>) {
  useEffect(() => {
    if (hidden) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (giphyPreview && e.key === "Escape") {
        e.preventDefault();
        onCancelGiphy();
        return;
      }

      if (showSlashCommands && filteredSlashCommands.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSlashIndex((i) => (i + 1) % filteredSlashCommands.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSlashIndex(
            (i) =>
              (i - 1 + filteredSlashCommands.length) %
              filteredSlashCommands.length,
          );
          return;
        }
        if (e.key === "Tab" || e.key === "Enter") {
          e.preventDefault();
          insertSlashCommand(filteredSlashCommands[slashIndex]);
          return;
        }
        if (e.key === "Escape") {
          setShowSlashCommands(false);
          return;
        }
      }

      if (showMentions && filteredUsers.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionIndex((i) => (i + 1) % filteredUsers.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionIndex(
            (i) => (i - 1 + filteredUsers.length) % filteredUsers.length,
          );
          return;
        }
        if (e.key === "Tab" || e.key === "Enter") {
          e.preventDefault();
          insertMention(filteredUsers[mentionIndex]);
          return;
        }
        if (e.key === "Escape") {
          setShowMentions(false);
          return;
        }
      }

      if (showEmojiShortcodes && filteredEmojis.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setEmojiShortcodeIndex((i) => (i + 1) % filteredEmojis.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setEmojiShortcodeIndex(
            (i) => (i - 1 + filteredEmojis.length) % filteredEmojis.length,
          );
          return;
        }
        if (e.key === "Tab" || e.key === "Enter") {
          e.preventDefault();
          insertEmojiShortcode(filteredEmojis[emojiShortcodeIndex]);
          return;
        }
        if (e.key === "Escape") {
          setShowEmojiShortcodes(false);
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    hidden,
    giphyPreview,
    onCancelGiphy,
    showSlashCommands,
    setShowSlashCommands,
    showMentions,
    setShowMentions,
    showEmojiShortcodes,
    setShowEmojiShortcodes,
    filteredSlashCommands,
    filteredUsers,
    filteredEmojis,
    slashIndex,
    setSlashIndex,
    mentionIndex,
    setMentionIndex,
    emojiShortcodeIndex,
    setEmojiShortcodeIndex,
    insertSlashCommand,
    insertMention,
    insertEmojiShortcode,
  ]);
}
